import { ReactComponent as OutlinePlus } from "assets/svg/outline-plus.svg";
import { moveItemInArray } from "hooks/use-list-reordering";
import messages from "messages";
import { useCallback, useMemo } from "react";
import { Button } from "react-bootstrap";
import { AmplitudePageName, track } from "utils/amplitude";
import getClasses from "utils/getClasses";
import SessionTypeListItem from "../Dashboard/DoctorDashboard/views/one-one-sessions/components/layout/components/calendar-view/components/session-type-list-item/SessionTypeListItem";
import styles from "./SessionTypesUI.module.scss";

const ANALYTICS_COMPONENT_NAME = "session-types-ui";

type Props = {
  sessionTypes: SessionType[];
  onApply(v: SessionType, i: number): void;
  onDelete?(v: SessionType, i: number): void;
  onEdit(v: SessionType, i: number): void;
  onAdd?: () => void;
  setSessionTypes: React.Dispatch<React.SetStateAction<SessionType[]>>;
  analyticsPageName?: AmplitudePageName;
  variant?: "default" | "compact" | "mobile";
};

/**
 * renders session types ui
 */
const SessionTypesUI = ({
  sessionTypes,
  onApply,
  onDelete,
  onEdit,
  onAdd,
  setSessionTypes,
  analyticsPageName,
  variant = "default",
}: Props) => {
  const isEmpty = useMemo(() => sessionTypes.length === 0, [sessionTypes]);

  const onReorder = useCallback(
    (fromIndex, toIndex) => {
      setSessionTypes((prev) => {
        const newOrder = moveItemInArray(prev, fromIndex, toIndex);
        return newOrder.map((el, i) => ({
          ...el,
          order: i,
        }));
      });
    },
    [setSessionTypes],
  );

  return (
    <div
      className={getClasses(
        styles["container"],
        variant === "compact" && styles["container--compact"],
      )}
    >
      {isEmpty ? (
        <div className={styles["no-session-types-wrapper"]}>
          <label className={styles["no-session-types"]}>
            {messages.noSessionTypes}
          </label>
        </div>
      ) : (
        <ul className={styles["session-types-list"]}>
          {sessionTypes.map((el, i) => (
            <SessionTypeListItem
              key={el.label}
              sessionType={el}
              index={i}
              onChange={(v) => onApply(v, i)}
              onEdit={() => onEdit(el, i)}
              onDelete={onDelete ? (v) => onDelete(v, i) : undefined}
              onReorder={onReorder}
              variant={variant}
            />
          ))}
        </ul>
      )}

      {onAdd && (
        <Button
          variant="link"
          className={styles["create-session-type-button"]}
          size="lg"
          onClick={() => {
            if (analyticsPageName) {
              track(
                analyticsPageName,
                [ANALYTICS_COMPONENT_NAME, "add-new-session-type-buton"],
                "click",
              );
            }
            onAdd();
          }}
        >
          <OutlinePlus />
          <span className={styles["create-session-type-button__label"]}>
            {messages.addANewSessionType}
          </span>
        </Button>
      )}
    </div>
  );
};

export default SessionTypesUI;
